import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../App";
import axios from "axios";
import { Plus, Trash2, Edit2, Users, AlertTriangle, Check, Clock, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Switch } from "@/components/ui/switch";

const CariHesaplar = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState("hepsi");
  const [selectedForReminder, setSelectedForReminder] = useState(null);
  const [reminderEmail, setReminderEmail] = useState("");
  const [reminderName, setReminderName] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [form, setForm] = useState({
    musteri_adi: "",
    tip: "alacak",
    tutar: "",
    vade_tarihi: null,
    aciklama: "",
    tarih: new Date(),
    odendi: false
  });

  useEffect(() => {
    fetchItems();
    checkEmailStatus();
  }, []);

  const checkEmailStatus = async () => {
    try {
      const response = await axios.get(`${API}/email/status`, { withCredentials: true });
      setEmailConfigured(response.data.configured);
    } catch (error) {
      setEmailConfigured(false);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/cari-hesaplar`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const openReminderDialog = (item) => {
    setSelectedForReminder(item);
    setReminderEmail("");
    setReminderName(item.musteri_adi);
    setReminderDialogOpen(true);
  };

  const sendReminder = async () => {
    if (!reminderEmail || !reminderName) {
      toast.error("Lütfen e-posta ve isim alanlarını doldurun");
      return;
    }

    setSendingReminder(true);
    try {
      await axios.post(`${API}/email/send-reminder`, {
        recipient_email: reminderEmail,
        recipient_name: reminderName,
        musteri_adi: selectedForReminder.musteri_adi,
        tutar: selectedForReminder.tutar,
        vade_tarihi: selectedForReminder.vade_tarihi 
          ? new Date(selectedForReminder.vade_tarihi).toLocaleDateString('tr-TR')
          : "Belirtilmemiş"
      }, { withCredentials: true });

      toast.success("Hatırlatma e-postası gönderildi!");
      setReminderDialogOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || "E-posta gönderilemedi";
      toast.error(message);
    } finally {
      setSendingReminder(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.musteri_adi || !form.tutar) {
      toast.error("Lütfen gerekli alanları doldurun");
      return;
    }

    try {
      const payload = {
        ...form,
        tutar: parseFloat(form.tutar),
        tarih: form.tarih.toISOString(),
        vade_tarihi: form.vade_tarihi ? form.vade_tarihi.toISOString() : null
      };

      if (editingId) {
        await axios.put(`${API}/cari-hesaplar/${editingId}`, payload, { withCredentials: true });
        toast.success("Cari hesap güncellendi");
      } else {
        await axios.post(`${API}/cari-hesaplar`, payload, { withCredentials: true });
        toast.success("Cari hesap eklendi");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error("İşlem sırasında hata oluştu");
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      musteri_adi: item.musteri_adi,
      tip: item.tip,
      tutar: item.tutar.toString(),
      vade_tarihi: item.vade_tarihi ? new Date(item.vade_tarihi) : null,
      aciklama: item.aciklama || "",
      tarih: new Date(item.tarih),
      odendi: item.odendi
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/cari-hesaplar/${id}`, { withCredentials: true });
      toast.success("Kayıt silindi");
      fetchItems();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      musteri_adi: "",
      tip: "alacak",
      tutar: "",
      vade_tarihi: null,
      aciklama: "",
      tarih: new Date(),
      odendi: false
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(value || 0);
  };

  const isOverdue = (vadeTarihi, odendi) => {
    if (odendi || !vadeTarihi) return false;
    return new Date(vadeTarihi) < new Date();
  };

  const getStatusBadge = (item) => {
    if (item.odendi) {
      return <span className="badge-paid"><Check size={12} className="mr-1" />Ödendi</span>;
    }
    if (isOverdue(item.vade_tarihi, item.odendi)) {
      return <span className="badge-overdue"><AlertTriangle size={12} className="mr-1" />Vadesi Geçti</span>;
    }
    return <span className="badge-pending"><Clock size={12} className="mr-1" />Bekliyor</span>;
  };

  let filteredItems = items;
  if (filter === "alacak") filteredItems = items.filter(i => i.tip === "alacak");
  else if (filter === "borc") filteredItems = items.filter(i => i.tip === "borc");
  else if (filter === "vadesi-gecen") filteredItems = items.filter(i => isOverdue(i.vade_tarihi, i.odendi));

  const toplamAlacak = items.filter(i => i.tip === "alacak" && !i.odendi).reduce((sum, i) => sum + i.tutar, 0);
  const toplamBorc = items.filter(i => i.tip === "borc" && !i.odendi).reduce((sum, i) => sum + i.tutar, 0);
  const vadesiGecenToplam = items.filter(i => isOverdue(i.vade_tarihi, i.odendi)).reduce((sum, i) => sum + i.tutar, 0);

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="cari-hesaplar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0B] tracking-tight">
              Cari Hesaplar
            </h1>
            <p className="text-[#5E5E62] text-sm mt-1">
              Alacak ve borçlarınızı takip edin
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button className="btn-primary flex items-center gap-2" data-testid="add-cari-btn">
                <Plus size={18} />
                Yeni Kayıt
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingId ? "Cari Hesap Düzenle" : "Yeni Cari Hesap"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Tip Selection */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, tip: "alacak" })}
                    className={`py-3 rounded-sm font-medium transition-colors ${
                      form.tip === "alacak"
                        ? "bg-[#E6F9F0] text-[#00C566] border-2 border-[#00C566]"
                        : "bg-[#F7F7F8] text-[#5E5E62] border border-[#E4E4E7]"
                    }`}
                    data-testid="select-alacak-btn"
                  >
                    Alacak
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, tip: "borc" })}
                    className={`py-3 rounded-sm font-medium transition-colors ${
                      form.tip === "borc"
                        ? "bg-[#FFEBEE] text-[#FF3B30] border-2 border-[#FF3B30]"
                        : "bg-[#F7F7F8] text-[#5E5E62] border border-[#E4E4E7]"
                    }`}
                    data-testid="select-borc-btn"
                  >
                    Borç
                  </button>
                </div>

                {/* Müşteri Adı */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Müşteri / Firma Adı</label>
                  <input
                    type="text"
                    value={form.musteri_adi}
                    onChange={(e) => setForm({ ...form, musteri_adi: e.target.value })}
                    className="input-default"
                    placeholder="Müşteri veya firma adı"
                    data-testid="musteri-input"
                  />
                </div>

                {/* Tutar */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Tutar (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.tutar}
                    onChange={(e) => setForm({ ...form, tutar: e.target.value })}
                    className="input-default"
                    placeholder="0.00"
                    data-testid="tutar-input"
                  />
                </div>

                {/* Tarihler */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">İşlem Tarihi</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="input-default text-left" data-testid="tarih-picker">
                          {format(form.tarih, "d MMM yyyy", { locale: tr })}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.tarih}
                          onSelect={(date) => date && setForm({ ...form, tarih: date })}
                          locale={tr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Vade Tarihi</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="input-default text-left" data-testid="vade-picker">
                          {form.vade_tarihi ? format(form.vade_tarihi, "d MMM yyyy", { locale: tr }) : "Seçiniz"}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.vade_tarihi}
                          onSelect={(date) => setForm({ ...form, vade_tarihi: date })}
                          locale={tr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Açıklama */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Açıklama</label>
                  <input
                    type="text"
                    value={form.aciklama}
                    onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                    className="input-default"
                    placeholder="Opsiyonel"
                    data-testid="aciklama-input"
                  />
                </div>

                {/* Ödendi Switch */}
                <div className="flex items-center justify-between py-2">
                  <label className="text-sm font-medium text-[#0A0A0B]">Ödendi mi?</label>
                  <Switch
                    checked={form.odendi}
                    onCheckedChange={(checked) => setForm({ ...form, odendi: checked })}
                    data-testid="odendi-switch"
                  />
                </div>

                <button type="submit" className="btn-primary w-full" data-testid="submit-cari-btn">
                  {editingId ? "Güncelle" : "Kaydet"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users size={18} className="text-[#00C566]" />
              <span className="text-sm text-[#5E5E62]">Toplam Alacak</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#00C566]">{formatCurrency(toplamAlacak)}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users size={18} className="text-[#FF3B30]" />
              <span className="text-sm text-[#5E5E62]">Toplam Borç</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#FF3B30]">{formatCurrency(toplamBorc)}</p>
          </div>
          <div className={`border rounded-sm p-4 ${vadesiGecenToplam > 0 ? 'bg-[#FFEBEE] border-[#FF3B30]' : 'bg-white border-[#E4E4E7]'}`}>
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={18} className="text-[#FF3B30]" />
              <span className="text-sm text-[#5E5E62]">Vadesi Geçen</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#FF3B30]">{formatCurrency(vadesiGecenToplam)}</p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { key: "hepsi", label: "Hepsi" },
            { key: "alacak", label: "Alacaklar" },
            { key: "borc", label: "Borçlar" },
            { key: "vadesi-gecen", label: "Vadesi Geçen" }
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                filter === f.key
                  ? f.key === "vadesi-gecen" ? "bg-[#FF3B30] text-white" : "bg-[#002FA7] text-white"
                  : "bg-white text-[#5E5E62] border border-[#E4E4E7] hover:bg-[#F7F7F8]"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cari-table">
              <thead>
                <tr className="border-b border-[#E4E4E7]">
                  <th className="table-header">Tip</th>
                  <th className="table-header">Müşteri/Firma</th>
                  <th className="table-header">Tutar</th>
                  <th className="table-header">İşlem Tarihi</th>
                  <th className="table-header">Vade Tarihi</th>
                  <th className="table-header">Durum</th>
                  <th className="table-header w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-[#5E5E62]">
                      Henüz kayıt bulunmuyor
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr 
                      key={item.id} 
                      className={`table-row ${isOverdue(item.vade_tarihi, item.odendi) ? 'bg-[#FFEBEE]/50' : ''}`}
                    >
                      <td className="table-cell">
                        <span className={item.tip === "alacak" ? "badge-paid" : "badge-overdue"}>
                          {item.tip === "alacak" ? "Alacak" : "Borç"}
                        </span>
                      </td>
                      <td className="table-cell font-medium">{item.musteri_adi}</td>
                      <td className={`table-cell font-mono ${item.tip === "alacak" ? "text-[#00C566]" : "text-[#FF3B30]"}`}>
                        {formatCurrency(item.tutar)}
                      </td>
                      <td className="table-cell font-mono text-[#5E5E62]">
                        {new Date(item.tarih).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="table-cell font-mono text-[#5E5E62]">
                        {item.vade_tarihi ? new Date(item.vade_tarihi).toLocaleDateString('tr-TR') : "-"}
                      </td>
                      <td className="table-cell">{getStatusBadge(item)}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1">
                          {item.tip === "alacak" && !item.odendi && (
                            <button
                              onClick={() => openReminderDialog(item)}
                              className="p-2 text-[#002FA7] hover:bg-[#F7F7F8] rounded-sm transition-colors"
                              title="Hatırlatma E-postası Gönder"
                              data-testid={`reminder-${item.id}`}
                            >
                              <Mail size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => handleEdit(item)}
                            className="p-2 text-[#5E5E62] hover:bg-[#F7F7F8] rounded-sm transition-colors"
                            data-testid={`edit-${item.id}`}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="p-2 text-[#FF3B30] hover:bg-[#FFEBEE] rounded-sm transition-colors"
                            data-testid={`delete-${item.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Email Reminder Dialog */}
        <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Mail size={20} className="text-[#002FA7]" />
                Hatırlatma E-postası Gönder
              </DialogTitle>
            </DialogHeader>
            
            {!emailConfigured ? (
              <div className="alert-warning mt-4">
                <p className="text-sm font-medium text-[#FF9500]">E-posta servisi yapılandırılmamış</p>
                <p className="text-xs text-[#5E5E62] mt-1">
                  E-posta göndermek için backend/.env dosyasına RESEND_API_KEY eklemeniz gerekiyor.
                  <br />
                  <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] underline">
                    resend.com
                  </a> adresinden ücretsiz API anahtarı alabilirsiniz.
                </p>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                {selectedForReminder && (
                  <div className="bg-[#F7F7F8] p-4 rounded-sm">
                    <p className="text-xs uppercase tracking-[0.1em] text-[#5E5E62] font-semibold mb-2">Alacak Bilgisi</p>
                    <p className="font-medium text-[#0A0A0B]">{selectedForReminder.musteri_adi}</p>
                    <p className="font-mono text-lg text-[#FF3B30]">{formatCurrency(selectedForReminder.tutar)}</p>
                    {selectedForReminder.vade_tarihi && (
                      <p className="text-sm text-[#5E5E62] mt-1">
                        Vade: {new Date(selectedForReminder.vade_tarihi).toLocaleDateString('tr-TR')}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Alıcı Adı</label>
                  <input
                    type="text"
                    value={reminderName}
                    onChange={(e) => setReminderName(e.target.value)}
                    className="input-default"
                    placeholder="Alıcının adı"
                    data-testid="reminder-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">E-posta Adresi</label>
                  <input
                    type="email"
                    value={reminderEmail}
                    onChange={(e) => setReminderEmail(e.target.value)}
                    className="input-default"
                    placeholder="ornek@email.com"
                    data-testid="reminder-email-input"
                  />
                </div>

                <button
                  onClick={sendReminder}
                  disabled={sendingReminder || !reminderEmail}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="send-reminder-btn"
                >
                  {sendingReminder ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Hatırlatma Gönder
                    </>
                  )}
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CariHesaplar;
